import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import BackButton from "../../../../components/BackButton";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { getWithdrawSteps } from "../../constants/steps";
import {
  getManualProfile,
  ManualProfileResponse,
  updateWithdrawStatus,
} from "../../../../services/withdrawApiService";
import EditIcon from "../../../../assets/icons/edit.svg";

const LOCALE =
  "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep5ManualReview";

const WithdrawPageStep5ManualReview: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);
  const navigate = useNavigate();

  const [profile, setProfile] = useState<ManualProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const storedWithdrawal = localStorage.getItem("withdrawal");
  const withdrawID = storedWithdrawal
    ? (JSON.parse(storedWithdrawal).withdrawalID as string | undefined)
    : undefined;

  useEffect(() => {
    const load = async () => {
      if (!withdrawID) {
        setError(t("missingWithdrawId", "No se encontró el retiro activo."));
        setLoading(false);
        return;
      }
      try {
        const result = await getManualProfile(withdrawID);
        if (!result) {
          setError(t("missingProfile", "No encontramos tus datos. Captúralos primero."));
        } else {
          setProfile(result);
        }
      } catch (err) {
        console.error("Error fetching manual profile", err);
        setError(t("loadError", "No pudimos cargar tus datos. Intenta más tarde."));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [withdrawID, t]);

  const handleEdit = () => navigate("/my-table/withdraw/step4-manual");
  const handleConfirm = async () => {
    if (!withdrawID) return;
    try {
      await updateWithdrawStatus({
        withdrawID,
        status: "pending_review",
      });
      navigate("/my-table/withdraw/step6-manual");
    } catch (err) {
      setError(t("statusError", "No pudimos actualizar el estado del retiro."));
    }
  };
  const handleBack = () => navigate("/my-table/withdraw/step3-manual");

  const renderRow = (label: string, value?: string) =>
    value ? (
      <div className="flex justify-between text-sm text-gray-700">
        <span className="font-semibold text-gray-900">{label}</span>
        <span className="text-right">{value}</span>
      </div>
    ) : null;

  return (
    <PageContainer>
      <div className="mt-16" />
      <StepIndicator steps={steps} currentStep={4} progressPercentage={90} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text={t("backButton", "Regresar")} onClick={handleBack} />
        <h2 className="text-2xl font-bold mb-4 text-primary">
          {t("reviewHeading", "Confirma tus datos")}
        </h2>
        {loading ? (
          <div className="text-gray-600">{t("loading", "Cargando...")}</div>
        ) : error ? (
          <Alert message={error} level="error" />
        ) : profile ? (
          <div className="space-y-4">
            <div className="bg-background p-4 rounded space-y-2">
              {renderRow(t("bankLabel", "Banco / Cuenta"), `${profile.bank || ""} ${profile.account || ""}`)}
              {renderRow(t("clabeLabel", "CLABE"), profile.clabe)}
              {renderRow(t("holderLabel", "Titular/Representante"), profile.holderName)}
              {renderRow(t("rfcLabel", "RFC"), profile.rfc)}
              {renderRow(t("contactLabel", "Contacto"), `${profile.contactEmail || ""} ${profile.contactPhone || ""}`)}
              {renderRow(t("dobLabel", "Fecha de nacimiento"), profile.dateOfBirth)}
              {renderRow(
                t("addressLabel", "Dirección"),
                `${profile.addressLine1 || ""} ${profile.addressLine2 || ""}, ${profile.city || ""}, ${
                  profile.state || ""
                }, ${profile.postalCode || ""}, ${profile.country || ""}`
              )}
            </div>
            <div className="flex flex-col gap-3">
              <Button
                label={t("confirmButton", "Confirmar datos")}
                onClick={handleConfirm}
                type="primary"
                className="w-full"
              />
              <Button
                label={t("editButton", "Editar")}
                icon={<img src={EditIcon} alt="" className="w-4 h-4" />}
                onClick={handleEdit}
                type="secondary"
                className="w-full"
              />
            </div>
          </div>
        ) : null}
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep5ManualReview));
